/* Scheduler — Phase 3 item 3.8 UI
 *
 * Manual trigger + status + run history for the background scanner.
 * Visible to admins under /admin/scheduler.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Loader2, RefreshCw, PlayCircle, Clock, Activity, AlertCircle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { API } from '../../lib/api';

export default function Scheduler() {
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/scheduler/status`),
        axios.get(`${API}/scheduler/runs?limit=20`),
      ]);
      setStatus(s.data);
      setRuns(r.data?.items || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      await axios.post(`${API}/scheduler/run-now`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setRunning(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="scheduler-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Chivo'] flex items-center gap-2">
            <Clock className="w-6 h-6 text-primary" />
            Scheduled scanner
            <Badge variant="outline" className="text-[10px] ml-2">3.8</Badge>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Closes the Cloud Custodian gap. Re-runs Arbitrage / Egress / Policies
            on a cron and persists deltas to the Outcome Ledger.
            <span className="ml-1 italic">Enable via <code className="font-mono">SCHEDULER_ENABLED=true</code> env.</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={runNow} disabled={running} size="sm" data-testid="scheduler-run-now">
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <PlayCircle className="w-3.5 h-3.5 mr-1.5" />}
            Run now
          </Button>
          <Button onClick={load} disabled={loading} size="sm" variant="outline">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-red-500/40 bg-red-500/5">
          <CardContent className="p-4 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5" />
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Status */}
      {status && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Card className={status.running ? 'border-emerald-500/30 bg-emerald-500/5' : ''}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Status</p>
              <p className={`text-2xl font-bold mt-1 ${status.running ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}
                  data-testid="scheduler-status">
                {status.running ? 'RUNNING' : 'STOPPED'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">interval: {status.interval_seconds}s</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Run count</p>
              <p className="text-3xl font-bold mt-1 text-primary">{status.run_count}</p>
              <p className="text-xs text-muted-foreground mt-1">since process start</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Last run</p>
              <p className="text-sm font-semibold mt-1">{status.last_run ? new Date(status.last_run.started_at).toLocaleString() : '—'}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {status.last_run ? `${status.last_run.duration_seconds?.toFixed(2)}s · ${status.last_run.tenants_scanned} tenants` : 'no runs yet'}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">History</p>
              <p className="text-3xl font-bold mt-1 text-primary">{runs.length}</p>
              <p className="text-xs text-muted-foreground mt-1">persisted scans</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* History */}
      {runs.length > 0 && (
        <Card data-testid="scheduler-runs-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" /> Run history
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left px-4 py-2">Started</th>
                  <th className="text-right px-4 py-2">Duration</th>
                  <th className="text-right px-4 py-2">Tenants</th>
                  <th className="text-left px-4 py-2">Engines</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50">
                {runs.map((r, i) => {
                  const engines = Object.keys((r.tenants?.[0] || {}).engines || {});
                  return (
                    <tr key={r.scan_id || i} className="hover:bg-secondary/30"
                        data-testid={`scheduler-run-${i}`}>
                      <td className="px-4 py-1.5 tabular-nums">{new Date(r.started_at).toLocaleString()}</td>
                      <td className="px-4 py-1.5 text-right tabular-nums text-muted-foreground">{r.duration_seconds?.toFixed(2)}s</td>
                      <td className="px-4 py-1.5 text-right tabular-nums">{r.tenants_scanned}</td>
                      <td className="px-4 py-1.5 text-muted-foreground">{engines.join(' · ')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
